import React from 'react';
import './LandingPage.css';

function LandingPage({ onLogin }) {
  const handleBookMeeting = () => {
    window.open('https://calendly.com/aithisjob', '_blank');
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <img src="/logo.png" alt="Quote This Job" className="hero-logo" />
          <h1 className="hero-headline">Price jobs with confidence—using your own history.</h1>
          <p className="hero-subheadline">
            Turn years of past quotes into an AI-assisted estimator that helps your team create faster, more consistent, more profitable bids.
          </p>
          <ul className="value-bullets">
            <li>Learns from your past quotes and invoices</li>
            <li>Explains why a price is suggested</li>
            <li>Keeps humans in control—always</li>
          </ul>
          <div className="cta-buttons">
            <button className="btn btn-primary" onClick={handleBookMeeting}>Book a demo</button>
            <button className="btn btn-secondary" onClick={handleBookMeeting}>Talk to us</button>
          </div>
          {onLogin && (
            <button className="login-link" onClick={onLogin}>
              Client login →
            </button>
          )}
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="container">
          <h2>Your best estimator can't be on every quote.</h2>
          <div className="problem-content">
            <p>In many service businesses, pricing lives in people's heads.</p>
            <p>That works—until:</p>
            <ul>
              <li>Your senior estimator is overloaded</li>
              <li>Quotes get rushed</li>
              <li>Margins quietly erode</li>
              <li>New team members guess instead of knowing</li>
            </ul>
            <p className="problem-conclusion">
              Templates and rule-based software can't capture experience.<br />
              Spreadsheets don't scale.<br />
              And underpricing just one job can wipe out months of profit.
            </p>
          </div>
        </div>
      </section>

      {/* Insight Section */}
      <section className="insight-section">
        <div className="container">
          <h2>You already have the data. We help you use it.</h2>
          <p>Most businesses have years of historical quotes and invoices—but that knowledge is locked away.</p>
          <p>We turn your past jobs into a pricing intelligence layer that:</p>
          <ul className="insight-list">
            <li>Finds similar jobs automatically</li>
            <li>Learns how you actually price work</li>
            <li>Highlights risk before you send the quote</li>
          </ul>
          <p className="insight-tagline">
            No black boxes. No "set it and forget it."<br />
            Just better decisions, faster.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <h2>How it works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Import your past quotes</h3>
              <p>Upload historical quotes and invoices. We organize and learn from them.</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Describe a new job</h3>
              <p>Enter a short description—just like you already do.</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Get an assisted draft</h3>
              <p>We surface similar past jobs, suggest pricing ranges, and explain the reasoning.</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>You review and approve</h3>
              <p>Edit anything. Final decisions always stay with you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiator */}
      <section className="differentiator">
        <div className="container">
          <h2>Not job management software. Pricing intelligence.</h2>
          <div className="comparison">
            <div className="comparison-column">
              <h3>Most tools focus on:</h3>
              <ul>
                <li>Scheduling</li>
                <li>Dispatch</li>
                <li>Templates</li>
                <li>Speed</li>
              </ul>
            </div>
            <div className="comparison-column highlight">
              <h3>We focus on:</h3>
              <ul>
                <li>Pricing accuracy</li>
                <li>Margin protection</li>
                <li>Consistency across estimators</li>
                <li>Preserving institutional knowledge</li>
              </ul>
            </div>
          </div>
          <p className="differentiator-tagline">
            We don't run your business.<br />
            We help you price jobs like your most experienced estimator would.
          </p>
        </div>
      </section>

      {/* Integration */}
      <section className="integration-section">
        <div className="container">
          <h2>Works alongside the tools you already use</h2>
          <p>If you already use job management software, great.</p>
          <p>Our platform is designed to:</p>
          <ul>
            <li>Plug into your existing workflow</li>
            <li>Improve quotes before they're sent</li>
            <li>Make pricing decisions clearer—not more complex</li>
          </ul>
          <p className="integration-tagline">
            Think of it as an AI estimator—not another system your team has to learn from scratch.
          </p>
        </div>
      </section>

      {/* Who It's For */}
      <section className="target-section">
        <div className="container">
          <h2>Built for complex, experience-driven work</h2>
          <div className="target-content">
            <p><strong>Ideal for:</strong></p>
            <ul>
              <li>Commercial construction</li>
              <li>Specialty trades</li>
              <li>RV / marine repair</li>
              <li>Restoration and remodeling</li>
              <li>Any business where quoting accuracy matters</li>
            </ul>
            <p className="target-tagline">
              If your pricing depends on judgment, experience, and nuance—this is for you.
            </p>
          </div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="trust-section">
        <div className="container">
          <h2>Designed for trust, not autopilot</h2>
          <ul className="trust-list">
            <li>Humans approve every quote</li>
            <li>Pricing logic is explainable</li>
            <li>Conservative by default</li>
            <li>Learns from real outcomes over time</li>
          </ul>
          <p className="trust-tagline">No surprise pricing. No risky automation.</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <h2>Stop guessing. Start quoting with confidence.</h2>
          <div className="cta-buttons">
            <button className="btn btn-primary" onClick={handleBookMeeting}>Book a demo</button>
            <button className="btn btn-secondary" onClick={handleBookMeeting}>Talk to us</button>
          </div>
          <p className="microcopy">
            Early access is limited. We're working closely with a small number of businesses to shape the product.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p className="footer-tagline">
            AI-assisted quoting that learns from your past jobs and helps you price work with confidence.
          </p>
          <p className="footer-copy">&copy; 2025 Quote This Job. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
